import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useFormStatusFlows, formStatusLabel as flowLabel } from '@/hooks/useFormStatusFlows';

export const STRUCTURED_PERMIT_APPLICATION_ID = 'structured_permit_application';
export const STRUCTURED_COMPLETION_REPORT_ID = 'structured_completion_report';

const STATUS_STYLES = {
  empty: 'bg-white border-gray-300 text-gray-600 hover:border-[#003366]/40',
  draft: 'bg-amber-100 border-amber-400 text-amber-800 hover:bg-amber-200',
  sent: 'bg-blue-100 border-blue-400 text-blue-800 hover:bg-blue-200',
  signed: 'bg-indigo-100 border-indigo-400 text-indigo-800 hover:bg-indigo-200',
  submitted_for_approval: 'bg-purple-100 border-purple-400 text-purple-800 hover:bg-purple-200',
  pending: 'bg-orange-100 border-orange-400 text-orange-800 hover:bg-orange-200',
  stamped: 'bg-cyan-100 border-cyan-400 text-cyan-800 hover:bg-cyan-200',
  certify: 'bg-teal-100 border-teal-400 text-teal-800 hover:bg-teal-200',
  done: 'bg-emerald-100 border-emerald-400 text-emerald-800 hover:bg-emerald-200',
  granted: 'bg-emerald-100 border-emerald-400 text-emerald-800 hover:bg-emerald-200',
};

// Legacy status values written before this feature existed — map them into
// the current 6-step vocabulary (empty/draft/sent/signed/submitted_for_approval/
// pending/granted) so old data still renders sensibly. New intermediate
// steps (signed/submitted_for_approval/pending) pass through unchanged.
export const normalizeFormStatus = (s) => {
  const v = (s || '').toLowerCase();
  if (['granted', 'completed', 'filled', 'done', 'approved'].includes(v)) return 'granted';
  if (['signed'].includes(v)) return 'signed';
  if (['submitted_for_approval', 'submitted'].includes(v)) return 'submitted_for_approval';
  if (['pending'].includes(v)) return 'pending';
  if (['sent', 'viewed', 'viewed_by_customer'].includes(v)) return 'sent';
  if (['draft'].includes(v)) return 'draft';
  return 'empty';
};

/**
 * FormsBattery — "la bateria de formas" (Alian). Renders ONE unified row of
 * badges for every form tied to a permit/Office record — both the built-in
 * structured forms (Permit Application / Completion Report, which keep their
 * own rich data + auto-fill) and any dynamic template uploaded in Jobs>Forms
 * (NOC, NTO, Credit Card Auth, etc.) — all in the single drag-and-drop order
 * defined in Jobs>Forms. Clicking a badge opens that form; the reference
 * number below links back to the project.
 */
export default function FormsBattery({ permit, onOpenForm, onRefreshPermit }) {
  const { t } = useTranslation();
  const { flowFor } = useFormStatusFlows();
  const formStatusLabel = (flowId, statusKey) => {
    const s = flowFor(flowId).find(x => x.key === statusKey);
    return s ? flowLabel(s) : t(`permits.battery.status.${statusKey}`);
  };
  const [templates, setTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!permit?.id) return;
    setLoading(true);
    try {
      const [tplRes, instRes] = await Promise.all([
        api.get('/custom-forms/templates'),
        api.get('/custom-forms/instances', { params: { linked_type: 'permit', linked_id: permit.id } }),
      ]);
      // Templates flagged as the OFFICIAL Permit Application/Completion
      // Report are excluded from the generic badge list below — their
      // status already lives in the two 'structured' badges (which now
      // render/send that exact official PDF). Showing them again as a
      // third badge would be the redundant info Alian asked to remove.
      // Also dedupe by NAME — a legacy duplicate upload sharing the same
      // name as another active template ("Permit Application" x2,
      // "Completion Report" x2) collapses into a single badge.
      const active = (tplRes.data || []).filter(t => t.is_active !== false && !t.official_role);
      const seenNames = new Set();
      const deduped = [];
      for (const tpl of active) {
        const key = (tpl.name || '').trim().toLowerCase();
        if (key && seenNames.has(key)) continue;
        if (key) seenNames.add(key);
        deduped.push(tpl);
      }
      setTemplates(deduped);
      setInstances(instRes.data || []);
    } catch {
      toast.error(t('errors.loadingError'));
    } finally {
      setLoading(false);
    }
  }, [permit?.id, t]);

  useEffect(() => { load(); }, [load]);

  const badges = templates.map(tpl => {
    if (tpl.id === STRUCTURED_PERMIT_APPLICATION_ID) {
      return {
        id: tpl.id, kind: 'structured', key: 'well_application',
        label: t('permits.battery.permitApplication'),
        status: normalizeFormStatus(permit?.well_application_status?.status),
        meta: permit?.well_application_status,
      };
    }
    if (tpl.id === STRUCTURED_COMPLETION_REPORT_ID) {
      return {
        id: tpl.id, kind: 'structured', key: 'completion_report',
        label: t('permits.battery.completionReport'),
        status: normalizeFormStatus(permit?.completion_report_status?.status),
        meta: permit?.completion_report_status,
      };
    }
    const tplInstances = instances.filter(i => i.template_id === tpl.id);
    const latest = tplInstances[0] || null;
    return {
      id: tpl.id, kind: 'template', label: tpl.name,
      status: latest?.status || 'empty',
      instanceId: latest?.id || null,
      meta: latest,
    };
  });

  const handleBadgeClick = (badge) => {
    if (!onOpenForm) return;
    if (badge.kind === 'structured') {
      onOpenForm({ kind: 'structured', formKey: badge.key, name: badge.label });
    } else {
      onOpenForm({ kind: 'template', templateId: badge.id, instanceId: badge.instanceId, name: badge.label, status: badge.status });
    }
  };

  // Alian (video "no puedo eliminar el Completion Report"): let him remove a
  // wrong/duplicate form directly from the battery. Structured forms
  // (Permit Application / Completion Report) are reset to 'empty' for THIS
  // permit only; dynamic template instances are deleted outright — never
  // touching the shared template used by other permits.
  const handleBadgeDelete = async (e, badge) => {
    e.stopPropagation();
    if (!window.confirm(t('permits.battery.confirmDeleteForm', 'Delete this form for this Office Work? This cannot be undone.'))) return;
    try {
      if (badge.kind === 'structured') {
        const pathPart = badge.key === 'well_application' ? 'well-application' : 'completion-report';
        await api.delete(`/permits/${permit.id}/${pathPart}`);
      } else if (badge.instanceId) {
        await api.delete(`/custom-forms/instances/${badge.instanceId}`);
      } else {
        return;
      }
      toast.success(t('success.deleted'));
      load();
      onRefreshPermit && onRefreshPermit();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.deletingError'));
    }
  };

  // Prompt AD (2026-09-21) — Office Workflow "REQUIRED FORMS": grilla de
  // tarjetas con contador "X of Y completed" y barra de progreso, en vez de
  // la fila de pastillas anterior. Misma logica de datos, distinto layout.
  const total = badges.length;
  const grantedCount = badges.filter(b => b.status === 'granted').length;
  const pct = total > 0 ? Math.round((grantedCount / total) * 100) : 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3" data-testid="forms-battery">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-600" data-testid="forms-battery-title">Required Forms</p>
        <span className="text-xs font-semibold text-[#003366]" data-testid="forms-battery-counter">{grantedCount} of {total} completed</span>
      </div>
      <div className="w-full h-1.5 bg-gray-200 rounded-full mb-3 overflow-hidden">
        <div className="h-full bg-[#003366] transition-all" style={{ width: `${pct}%` }} data-testid="forms-battery-progress" />
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-3">
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="forms-battery-row">
          {badges.map(badge => {
            const canDelete = badge.status !== 'empty' && (badge.kind === 'structured' || badge.instanceId);
            return (
              <div key={badge.id} className="relative group/badge">
                <button
                  type="button"
                  onClick={() => handleBadgeClick(badge)}
                  data-testid={`form-badge-${badge.id}`}
                  className={`w-full flex flex-col items-start gap-1.5 px-3 py-2.5 rounded-lg border-2 text-left transition-all hover:shadow-md hover:-translate-y-0.5 ${STATUS_STYLES[badge.status]} ${canDelete ? 'pr-7' : ''}`}
                  title={badge.meta?.sent_to ? t('permits.battery.sentTo', { email: badge.meta.sent_to, date: badge.meta.sent_at ? new Date(badge.meta.sent_at).toLocaleDateString() : '' }) : ''}
                >
                  <span className="flex items-center gap-1.5 text-sm font-semibold"><FileText className="w-3.5 h-3.5" /> {badge.label}</span>
                  <span className="text-[10px] opacity-80 font-medium">{formStatusLabel(badge.id, badge.status)}</span>
                </button>
                {canDelete && (
                  <button
                    type="button"
                    onClick={(e) => handleBadgeDelete(e, badge)}
                    data-testid={`form-badge-delete-${badge.id}`}
                    title={t('common.delete')}
                    className="absolute right-1.5 top-1.5 w-4 h-4 rounded-full flex items-center justify-center text-current opacity-50 hover:opacity-100 hover:bg-white/60"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
          {badges.length === 0 && (
            <span className="text-xs text-gray-400 col-span-full">{t('customForms.noTemplates')}</span>
          )}
        </div>
      )}
    </div>
  );
}
